import { useMemo, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { oneDark } from "@codemirror/theme-one-dark";
import { githubLight } from "@uiw/codemirror-theme-github";
import { EditorView } from "@codemirror/view";

interface Props {
  body: string;
  onBodyChange: (s: string) => void;
}

// Canonical body envelope persisted on the request row. Mirrors the
// shape every GraphQL client uses on the wire:
//   { "query": "...", "variables": { ... }, "operationName": "..." }
// We store `variables` as a JSON text in the editor so the user can
// drop in {{VAR}} tokens — the server stringifies on send.
interface Envelope { query: string; variables: string; operationName?: string }

function parseBody(b: string): Envelope {
  const s = b.trim();
  if (!s) return { query: "", variables: "{}" };
  if (s.startsWith("{")) {
    try {
      const p = JSON.parse(s) as { query?: string; variables?: unknown; operationName?: string };
      const vars = p.variables == null
        ? "{}"
        : typeof p.variables === "string"
        ? p.variables
        : JSON.stringify(p.variables, null, 2);
      return { query: p.query ?? "", variables: vars, operationName: p.operationName };
    } catch { /* fall through to plain text */ }
  }
  // Legacy text body — treat as a raw query.
  return { query: b, variables: "{}" };
}

function serialize(env: Envelope): string {
  return JSON.stringify({
    query: env.query,
    variables: env.variables,
    operationName: env.operationName,
  });
}

// Split editor: top is the query, bottom is the variables JSON.
// Both use CodeMirror; the query pane uses our existing JSON
// highlighter as a tolerable fallback for GraphQL syntax (full
// graphql-language support is a future pass).
export default function GraphQLBodyEditor({ body, onBodyChange }: Props) {
  const env = useMemo(() => parseBody(body), [body]);
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const [variablesError, setVariablesError] = useState<string | null>(null);

  const setQuery = (q: string) => onBodyChange(serialize({ ...env, query: q }));
  const setVariables = (v: string) => {
    onBodyChange(serialize({ ...env, variables: v }));
    if (!v.trim()) { setVariablesError(null); return; }
    try { JSON.parse(v); setVariablesError(null); }
    catch (e) { setVariablesError(String((e as Error).message)); }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="flex-1 min-h-[160px] flex flex-col min-w-0">
        <div className="text-[11px] muted-strong uppercase tracking-wider mb-1">Query</div>
        <div className="flex-1 min-h-0 rounded border border-border overflow-hidden">
          <CodeMirror
            value={env.query}
            theme={isDark ? oneDark : githubLight}
            extensions={[EditorView.lineWrapping]}
            onChange={setQuery}
            height="100%"
            basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
            className="cm-host h-full"
          />
        </div>
      </div>
      <div className="h-[180px] flex flex-col min-w-0">
        <div className="text-[11px] muted-strong uppercase tracking-wider mb-1 flex items-center justify-between">
          <span>Variables (JSON)</span>
          {variablesError && <span className="text-danger normal-case">JSON error: {variablesError}</span>}
        </div>
        <div className="flex-1 min-h-0 rounded border border-border overflow-hidden">
          <CodeMirror
            value={env.variables}
            theme={isDark ? oneDark : githubLight}
            extensions={[json(), EditorView.lineWrapping]}
            onChange={setVariables}
            height="100%"
            basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
            className="cm-host h-full"
          />
        </div>
      </div>
      <p className="text-xs muted">
        Sent as <code className="mono">POST application/json</code> with body <code className="mono">{"{\"query\":\"…\",\"variables\":{…}}"}</code>. Use <code className="mono">{"{{VAR}}"}</code> tokens anywhere — they're substituted at send time.
      </p>
    </div>
  );
}
